import React from 'react';
import useIntl from '@afosto/utils-react/intl/useIntl';
import Button from '@material-ui/core/Button';
import { FormattedMessage } from 'react-intl';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import useDataSourceContext from '../DataSource/useDataSourceContext';
import translations from './translations';
import styled from 'styled-components';
import DataGridSortSelectionItem from './DataGridSortSelectionItem';
import ContentDrawer from '../ContentDrawer';
import reorderArray from '@afosto/utils/array/reorderArray';

const ActionContainer = styled.div`
  align-items: center;
  display: flex;
  justify-content: space-between;
  margin-bottom: ${props => props.theme.spacing(2)}px;
`;

const DraggableContainer = styled.div`
  user-select: none;
`;

const DataGridSortSelection = () => {
  const intl = useIntl();
  const {
    showAdvancedSort,
    setShowAdvancedSort,
    sorting = {},
    views = {},
  } = useDataSourceContext();
  const { activeSort, setActiveSort, hasAvailableSortOptions, sortSelection } = sorting;
  const { defaultView } = views;

  /**
   * Close the sort selection drawer.
   */
  const handleCloseSort = () => {
    setShowAdvancedSort(false);
  };

  /**
   * Reset the sorting back to the sorting of the default view.
   */
  const handleResetSort = () => {
    setActiveSort(defaultView ? defaultView.sort : []);
  };

  /**
   * Handle the drag end event on the sort selections.
   * Set the new sort selections based on their new positions.
   * @param {Object} result
   */
  const handleDragEnd = result => {
    const { source, destination } = result;

    if (!destination) {
      return;
    }

    const repositionedSortList = reorderArray(activeSort, {
      start: source.index,
      end: destination.index,
    });

    setActiveSort(repositionedSortList);
  };

  /**
   * Return a single sort option selection element.
   * @param {Object|null} sort
   * @param {null|number} idx
   * @param {boolean} isDraggable
   * @returns {Component}
   */
  const getDataGridSortSelectionItem = (sort, idx, isDraggable = true) => {
    let isOpen = false;
    const isNewSort = sortSelection && sortSelection.isNew && sort.isNew;
    const isCurrentSelection = sortSelection && sortSelection.currentKey === sort.key;

    if (sortSelection && (isCurrentSelection || isNewSort)) {
      isOpen = true;
    }
    const selectionItem = <DataGridSortSelectionItem key={sort.key} sort={sort} isOpen={isOpen} />;

    if (!isDraggable) {
      return selectionItem;
    }

    return (
      <Draggable key={sort.key} draggableId={sort.key} index={idx} isDragDisabled={!!isOpen}>
        {(dragProvided, snapshot) => (
          <DraggableContainer
            ref={dragProvided.innerRef}
            isDragging={snapshot.isDragging}
            {...dragProvided.draggableProps}
            {...dragProvided.dragHandleProps}
          >
            {selectionItem}
          </DraggableContainer>
        )}
      </Draggable>
    );
  };

  return (
    <ContentDrawer
      isOpen={showAdvancedSort}
      handleClose={handleCloseSort}
      title={intl.formatMessage(translations.sortSelection.title)}
      drawerWidth="400px"
    >
      <ActionContainer>
        <Button variant="outlined" onClick={handleResetSort}>
          <FormattedMessage {...translations.sortSelection.resetButton} />
        </Button>
      </ActionContainer>
      <DragDropContext onDragEnd={handleDragEnd}>
        <Droppable droppableId="available">
          {provided => (
            <div ref={provided.innerRef}>
              {activeSort.map((sort, idx) => getDataGridSortSelectionItem(sort, idx))}
              {provided.placeholder}
            </div>
          )}
        </Droppable>
      </DragDropContext>
      {hasAvailableSortOptions() &&
        getDataGridSortSelectionItem({ key: 'create-sort', isNew: true }, null, false)}
    </ContentDrawer>
  );
};

export default DataGridSortSelection;
